import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';

import FormTributos from '../components/FormTributos';
import { resumoNcm, aplicarNcm, mensagemErro } from '../tributosApi';
import { SituacaoProdutoTag, formatarNcm, normalizarTributos } from '../util';

/**
 * Alteração em massa: aplica a mesma tributação a todos os produtos de um NCM.
 * Os produtos alterados e conferidos são sincronizados com a Retaguarda pelo eloNFCeMonitor.
 */
export default function AlteracaoNcm({ onAlteracao }) {
  const navigate = useNavigate();

  const [ncm, setNcm] = useState('');
  const [resumo, setResumo] = useState(null);
  const [form, setForm] = useState(normalizarTributos(null));
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [marcarConferido, setMarcarConferido] = useState(true);
  const [confirmar, setConfirmar] = useState(false);

  const pesquisar = async e => {
    if (e) e.preventDefault();
    const codigo = ncm.replace(/\D/g, '');
    if (codigo.length !== 8) {
      toast.warn('Informe o NCM com 8 dígitos.');
      return;
    }
    setCarregando(true);
    try {
      const dados = await resumoNcm(codigo);
      setResumo(dados);
      // Pré-preenche com a tributação mais frequente do NCM
      const base = normalizarTributos(dados.modelo);
      setForm({ ...base, id: null, codigoEan: '', descricao: '', cnpjCliente: '', registroProduto: null, ncm: codigo });
      if (!dados.totalProdutos) toast.info('Nenhum produto cadastrado com esse NCM.');
    } catch (err) {
      setResumo(null);
      toast.error(mensagemErro(err, 'Erro ao consultar o NCM.'));
    } finally {
      setCarregando(false);
    }
  };

  const aplicar = async () => {
    setSalvando(true);
    try {
      const r = await aplicarNcm(resumo.ncm, form, marcarConferido);
      toast.success(`Tributação aplicada a ${r.quantidade} produto(s). A Retaguarda será atualizada na próxima sincronização.`);
      setConfirmar(false);
      onAlteracao();
      await pesquisar();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao aplicar a tributação.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <h1>Alteração por NCM</h1>
          <p>Aplica a mesma tributação a todos os produtos de um NCM e sincroniza com a Retaguarda.</p>
        </div>
        <form className="tb-search-inline" onSubmit={pesquisar}>
          <i className="pi pi-search" />
          <input value={ncm} onChange={e => setNcm(e.target.value)} placeholder="NCM (8 dígitos)" inputMode="numeric" maxLength={10} />
          <button type="submit" className="tb-btn tb-btn-primary tb-btn-sm" disabled={carregando}>
            {carregando ? <i className="pi pi-spin pi-spinner" /> : 'Buscar'}
          </button>
        </form>
      </div>

      {resumo && resumo.totalProdutos > 0 ? (
        <div className="tb-split">
          <aside className="tb-card tb-side-info">
            <h2>NCM {formatarNcm(resumo.ncm)}</h2>
            <div className="tb-info-grid" style={{ marginTop: 12 }}>
              <div className="tb-info"><small>Produtos</small><span>{resumo.totalProdutos}</span></div>
              <div className="tb-info"><small>Conferidos</small><span>{resumo.conferidos}</span></div>
            </div>

            <h3>Tributações encontradas</h3>
            <ul className="tb-history">
              {resumo.perfis.map(p => (
                <li key={`${p.cstVenda}|${p.csosnVenda}|${p.cfopVenda}|${p.icmsAliquota}`}>
                  <span className="tb-tag tb-tag-info">{p.quantidade}</span>
                  <span className="tb-mono">
                    {p.cstVenda ? `CST ${p.cstVenda}` : ''}{p.cstVenda && p.csosnVenda ? ' · ' : ''}{p.csosnVenda ? `CSOSN ${p.csosnVenda}` : ''}
                    {!p.cstVenda && !p.csosnVenda ? 'Sem CST/CSOSN' : ''}
                  </span>
                  <small>{p.cfopVenda ? `CFOP ${p.cfopVenda} · ` : ''}ICMS {Number(p.icmsAliquota || 0).toLocaleString('pt-BR')}%</small>
                </li>
              ))}
            </ul>

            <h3>Produtos</h3>
            <ul className="tb-history tb-ncm-produtos">
              {resumo.produtos.slice(0, 50).map(p => (
                <li key={p.id}>
                  <SituacaoProdutoTag produto={p} />
                  <button type="button" className="tb-btn tb-btn-link" onClick={() => navigate(`/tributos/produtos/${p.id}`)}>
                    {p.descricao}
                  </button>
                  <small className="tb-mono">{p.codigoEan || `Reg. ${p.registroProduto || '—'}`}</small>
                </li>
              ))}
            </ul>
            {resumo.produtos.length > 50 ? <p className="tb-obs">Mostrando 50 de {resumo.produtos.length} produtos.</p> : null}
          </aside>

          <div className="tb-card tb-form-card">
            <FormTributos valor={form} onChange={setForm} modoNcm />

            <div className="tb-actions">
              <div className="tb-actions-left">
                <Checkbox inputId="marcarConferido" checked={marcarConferido} onChange={e => setMarcarConferido(e.checked)} />
                <label htmlFor="marcarConferido" style={{ marginLeft: 8, cursor: 'pointer' }}>
                  Marcar todos como conferidos (envia para a Retaguarda)
                </label>
              </div>
              <div className="tb-actions-right">
                <button type="button" className="tb-btn tb-btn-primary" onClick={() => setConfirmar(true)} disabled={salvando}>
                  <i className="pi pi-check-circle" /> Aplicar a {resumo.totalProdutos} produto(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!resumo && !carregando ? (
        <section className="tb-card">
          <div className="tb-empty">
            <i className="pi pi-sliders-h" />
            <span>Informe um NCM para ver os produtos e a tributação atual.</span>
          </div>
        </section>
      ) : null}

      <Dialog
        className="tb-dialog"
        header="Aplicar tributação ao NCM"
        visible={confirmar}
        onHide={() => setConfirmar(false)}
        style={{ width: '30rem', maxWidth: '95vw' }}
        footer={(
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setConfirmar(false)}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-primary" onClick={aplicar} disabled={salvando}>
              {salvando ? <i className="pi pi-spin pi-spinner" /> : null} Aplicar
            </button>
          </div>
        )}
      >
        {resumo ? (
          <p>
            A tributação informada substituirá a de <strong>{resumo.totalProdutos} produto(s)</strong> do NCM{' '}
            <strong>{formatarNcm(resumo.ncm)}</strong>
            {marcarConferido ? ', que serão marcados como conferidos e sincronizados com a Retaguarda.' : '.'}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}

AlteracaoNcm.propTypes = {
  onAlteracao: PropTypes.func.isRequired,
};
